import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Download as DownloadIcon,
  Apple,
  Monitor,
  CheckCircle2,
  ExternalLink,
  Github,
  Loader2,
} from "lucide-react";

const Download = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";
  const [downloadingPlatform, setDownloadingPlatform] = useState(null);

  const handleDownload = async (platform) => {
    setDownloadingPlatform(platform);
    try {
      // Simply redirect to our backend download endpoint
      // The backend will stream the file directly from GitHub with proper authentication
      window.location.href = `${API_BASE_URL}/api/download/latest/${platform}`;

    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download: ${error.message}`);
    } finally {
      // Reset after a short delay to allow download to start
      setTimeout(() => setDownloadingPlatform(null), 1000);
    }
  };

  const platforms = [
    {
      name: "Windows",
      icon: <Monitor className="h-8 w-8" />,
      description: "Windows 10 or later (64-bit)",
      version: "Latest Release",
      features: [
        "Auto-update support",
        "Bundled Node.js v22.18",
        "Easy installer (.exe)",
        "System tray integration",
      ],
      platform: "windows",
      downloadText: "Download for Windows",
      available: true,
    },
    {
      name: "macOS",
      icon: <Apple className="h-8 w-8" />,
      description: "macOS 11 (Big Sur) or later",
      version: "Apple Silicon",
      features: [
        "Universal binary",
        "Native Apple Silicon support",
        "DMG installer",
        "System tray integration",
      ],
      platform: "macos",
      downloadText: "Download for macOS",
      available: true,
    },
  ];

  const systemRequirements = [
    { label: "RAM", value: "4GB minimum, 8GB recommended" },
    { label: "Disk Space", value: "500MB for app + data" },
    { label: "Internet", value: "Required for AI APIs" },
    { label: "Node.js", value: "Bundled (v22.18)" },
  ];

  return (
    <div className="min-h-screen pt-20 pb-20">
      <section className="py-20 bg-gradient-to-b from-purple-950/10 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <h1 className="text-5xl md:text-6xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Download Cogsmith
              </span>
            </h1>
            <p className="text-xl text-foreground/70">
              Get started with Cogsmith on your platform of choice
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {platforms.map((platform, index) => (
              <Card
                key={index}
                className={`group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 ${
                  !platform.available ? "opacity-60" : ""
                }`}
              >
                <CardHeader className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform duration-300 mx-auto mb-4">
                    {platform.icon}
                  </div>
                  <CardTitle className="text-2xl">{platform.name}</CardTitle>
                  <p className="text-sm text-foreground/60">
                    {platform.description}
                  </p>
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-xs text-purple-400 mt-2">
                    {platform.version}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {platform.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start space-x-2 text-sm text-foreground/70"
                      >
                        <CheckCircle2 className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300"
                    disabled={!platform.available || downloadingPlatform !== null}
                    onClick={() => handleDownload(platform.platform)}
                  >
                    {downloadingPlatform === platform.platform ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Preparing Download...
                      </>
                    ) : (
                      <>
                        <DownloadIcon className="mr-2 h-4 w-4" />
                        {platform.downloadText}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://github.com/newtro/Cogsmith/releases"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                variant="outline"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50"
              >
                <Github className="mr-2 h-5 w-5" />
                View All Releases
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                System Requirements
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {systemRequirements.map((req, index) => (
                <Card
                  key={index}
                  className="bg-card/50 backdrop-blur-sm border-border/40"
                >
                  <CardContent className="p-6 flex items-center justify-between">
                    <span className="font-semibold text-foreground">
                      {req.label}
                    </span>
                    <span className="text-foreground/70 text-sm">
                      {req.value}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-background to-purple-950/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-card/50 backdrop-blur-sm border-border/40">
              <CardHeader>
                <CardTitle className="text-2xl">Installation Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-purple-400 mb-2 flex items-center">
                      <Monitor className="h-5 w-5 mr-2" />
                      Windows
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-foreground/70 ml-4">
                      <li>Download the .exe installer from releases</li>
                      <li>Run the installer and follow the prompts</li>
                      <li>Launch Cogsmith from Start Menu</li>
                      <li>Configure your AI provider and API key</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-purple-400 mb-2 flex items-center">
                      <Apple className="h-5 w-5 mr-2" />
                      macOS
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-foreground/70 ml-4">
                      <li>Download the .dmg file from releases</li>
                      <li>Open the DMG and drag Cogsmith to Applications</li>
                      <li>Launch Cogsmith from Applications</li>
                      <li>Configure your AI provider and API key</li>
                    </ol>
                  </div>
                </div>

                <div className="bg-purple-950/20 border border-purple-500/20 rounded-lg p-6">
                  <h3 className="font-semibold text-foreground mb-3">
                    Need Help?
                  </h3>
                  <p className="text-foreground/70 mb-4">
                    Check out the documentation or open an issue on GitHub if
                    you encounter any problems.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://github.com/newtro/Cogsmith/blob/main/README.md"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="outline"
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50"
                      >
                        Read Documentation
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                    <a
                      href="https://github.com/newtro/Cogsmith/issues"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="outline"
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50"
                      >
                        Report an Issue
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Download;